const menuContainerSelector = '[data-menu-container]';
const activeMenuContainerBgColour = "u-bg-alabaster-grey";
export class MenuHandler {
    constructor(config = {}) {
        this.menus = new Map();
        this.outsideClickListener = null;
        this.escapeKeyListener = null;
        this.isInitialized = false;
        this.retryCount = 0;
        this.mutationObserver = null;
        this.initializationTimeout = null;
        this.globalConfig = Object.assign({ hiddenClass: 'u-hidden', closeOnOutsideClick: true, closeOnEscape: true, returnFocusOnClose: true, autoInit: true, debug: false }, config);
        this.handlePageRefresh();
    }
    handlePageRefresh() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.closeAllMenusOnPageLoad();
            });
        }
        else {
            this.closeAllMenusOnPageLoad();
        }
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                console.log('MenuHandler: Page loaded from cache, resetting menu states');
                this.resetMenuStates();
            }
        });
    }
    closeAllMenusOnPageLoad() {
        const allMenus = document.querySelectorAll('[data-menu]');
        const hiddenClass = this.globalConfig.hiddenClass;
        allMenus.forEach(menu => {
            const menuElement = menu;
            if (!menuElement.classList.contains(hiddenClass)) {
                menuElement.classList.add(hiddenClass);
            }
        });
        const allTriggers = document.querySelectorAll('[data-menu-trigger]');
        allTriggers.forEach(trigger => {
            const triggerElement = trigger;
            triggerElement.setAttribute('aria-expanded', 'false');
        });
    }
    resetMenuStates() {
        this.menus.forEach(menuState => {
            menuState.isOpen = false;
            menuState.element.classList.add(menuState.config.hiddenClass);
            menuState.trigger.setAttribute('aria-expanded', 'false');
        });
    }
    init() {
        if (this.isInitialized) {
            console.warn('MenuHandler: Already initialized, skipping duplicate initialization');
            return;
        }
        this.retryCount = 0;
        this.waitForPartialViewsThenInitialize();
    }
    waitForPartialViewsThenInitialize() {
        const checkAndInitialize = () => {
            const menuContainers = document.querySelectorAll(menuContainerSelector);
            let allMenusFound = true;
            for (const container of menuContainers) {
                const containerElement = container;
                const trigger = containerElement.querySelector('[data-menu-trigger]');
                if (!trigger)
                    continue;
                const targetName = trigger.getAttribute('data-menu-trigger');
                if (!targetName)
                    continue;
                const menu = containerElement.querySelector(`[data-menu="${targetName}"]`);
                if (!menu) {
                    allMenusFound = false;
                    break;
                }
            }
            if (allMenusFound && menuContainers.length > 0) {
                if (this.mutationObserver) {
                    this.mutationObserver.disconnect();
                    this.mutationObserver = null;
                }
                if (this.initializationTimeout) {
                    clearTimeout(this.initializationTimeout);
                    this.initializationTimeout = null;
                }
                setTimeout(() => {
                    this.performInitialization();
                }, 100);
                return true;
            }
            return false;
        };
        if (checkAndInitialize()) {
            return;
        }
        this.mutationObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.addedNodes.length > 0) {
                    checkAndInitialize();
                    break;
                }
            }
        });
        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
        this.initializationTimeout = window.setTimeout(() => {
            if (this.mutationObserver) {
                this.mutationObserver.disconnect();
                this.mutationObserver = null;
            }
            this.performInitialization();
        }, 5000);
    }
    performInitialization() {
        const menuContainers = document.querySelectorAll(menuContainerSelector);
        let successCount = 0;
        let errorCount = 0;
        if (menuContainers.length > 0) {
            menuContainers.forEach((container, index) => {
                const containerElement = container;
                const containerName = containerElement.getAttribute(menuContainerSelector);
                const trigger = containerElement.querySelector('[data-menu-trigger]:not([data-menu-initialized])');
                if (!trigger) {
                    return;
                }
                const triggerElement = trigger;
                const targetName = triggerElement.getAttribute('data-menu-trigger');
                if (!targetName) {
                    errorCount++;
                    return;
                }
                const menuElement = containerElement.querySelector(`[data-menu="${targetName}"]`);
                if (!menuElement) {
                    errorCount++;
                    return;
                }
                try {
                    this.registerMenuFromElements(triggerElement, menuElement);
                    successCount++;
                }
                catch (error) {
                    errorCount++;
                }
            });
        }
        else {
            const triggers = document.querySelectorAll('[data-menu-trigger]:not([data-menu-initialized])');
            triggers.forEach((trigger, index) => {
                const triggerElement = trigger;
                const targetName = triggerElement.getAttribute('data-menu-trigger');
                if (!targetName) {
                    errorCount++;
                    return;
                }
                const menuElement = this.findMenuTarget(triggerElement, targetName);
                if (!menuElement) {
                    errorCount++;
                    return;
                }
                try {
                    this.registerMenuFromElements(triggerElement, menuElement);
                    successCount++;
                }
                catch (error) {
                    errorCount++;
                }
            });
        }
        this.setupCloseButtons();
        this.setupGlobalListeners();
        this.isInitialized = true;
        if (this.globalConfig.debug) {
            console.log(`MenuHandler: Initialization complete - ${successCount} menus registered successfully, ${errorCount} errors`);
        }
    }
    retryDeferredTriggers(deferredTriggers) {
        const maxRetries = 3;
        const retryDelay = 100;
        setTimeout(() => {
            let remainingTriggers = [];
            for (const { trigger, targetName, retryCount } of deferredTriggers) {
                const menuElement = this.findMenuTarget(trigger, targetName);
                if (menuElement) {
                    try {
                        this.registerMenuFromElements(trigger, menuElement);
                        console.log(`MenuHandler: Successfully registered deferred menu "${targetName}" on retry ${retryCount + 1}`);
                    }
                    catch (error) {
                        console.error(`MenuHandler: Failed to register deferred menu "${targetName}":`, error);
                    }
                }
                else if (retryCount < maxRetries) {
                    remainingTriggers.push({ trigger, targetName, retryCount: retryCount + 1 });
                }
                else {
                    console.error(`MenuHandler: Failed to find menu target "${targetName}" after ${maxRetries} retries`, trigger);
                }
            }
            if (remainingTriggers.length > 0) {
                this.retryDeferredTriggers(remainingTriggers);
            }
        }, retryDelay);
    }
    toggleMenu(menuId) {
        const menuState = this.menus.get(menuId);
        if (!menuState) {
            console.warn(`Menu with id "${menuId}" not found`);
            return;
        }
        if (menuState.isOpen) {
            this.closeMenu(menuId);
        }
        else {
            this.closeAllMenus(false);
            this.openMenu(menuId);
        }
    }
    openMenu(menuId) {
        const menuState = this.menus.get(menuId);
        if (!menuState) {
            console.warn(`Menu with id "${menuId}" not found`);
            return;
        }
        if (menuState.isOpen)
            return;
        menuState.element.classList.remove(menuState.config.hiddenClass);
        menuState.trigger.setAttribute('aria-expanded', 'true');
        menuState.isOpen = true;
        const triggerContainer = menuState.trigger.closest(menuContainerSelector);
        if (triggerContainer) {
            triggerContainer.classList.add(activeMenuContainerBgColour);
        }
        this.dispatchMenuEvent(menuId, 'opened', menuState);
    }
    closeMenu(menuId, returnFocus) {
        const menuState = this.menus.get(menuId);
        if (!menuState) {
            console.warn(`Menu with id "${menuId}" not found`);
            return;
        }
        if (!menuState.isOpen)
            return;
        if (this.globalConfig.debug && menuState.element.id === 'mobile-nav') {
            console.log('[MenuHandler] Closing mobile-nav menu');
            console.trace('[MenuHandler] Close stack trace:');
        }
        menuState.element.classList.add(menuState.config.hiddenClass);
        menuState.trigger.setAttribute('aria-expanded', 'false');
        menuState.isOpen = false;
        const triggerContainer = menuState.trigger.closest(menuContainerSelector);
        if (triggerContainer) {
            triggerContainer.classList.remove(activeMenuContainerBgColour);
        }
        const shouldReturnFocus = returnFocus !== undefined ? returnFocus : menuState.config.returnFocusOnClose;
        if (shouldReturnFocus) {
            setTimeout(() => {
                if (menuState.trigger && typeof menuState.trigger.focus === 'function') {
                    menuState.trigger.focus();
                }
            }, 10);
        }
        this.dispatchMenuEvent(menuId, 'closed', menuState);
    }
    closeAllMenus(returnFocus) {
        this.menus.forEach((_, menuId) => {
            this.closeMenu(menuId, returnFocus);
        });
    }
    isMenuOpen(menuId) {
        const menuState = this.menus.get(menuId);
        return menuState ? menuState.isOpen : false;
    }
    getOpenMenus() {
        const openMenus = [];
        this.menus.forEach((menuState, menuId) => {
            if (menuState.isOpen) {
                openMenus.push(menuId);
            }
        });
        return openMenus;
    }
    findMenuByElement(element) {
        for (const [menuId, menuState] of this.menus) {
            if (menuState.element === element || menuState.trigger === element) {
                return menuId;
            }
        }
        return null;
    }
    refresh() {
        const triggers = document.querySelectorAll('[data-menu-trigger]:not([data-menu-initialized])');
        if (triggers.length === 0) {
            console.log('MenuHandler: No new menu triggers found during refresh');
            this.setupCloseButtons();
            return;
        }
        console.log(`MenuHandler: Found ${triggers.length} new menu triggers during refresh`);
        let successCount = 0;
        let errorCount = 0;
        triggers.forEach((trigger, index) => {
            const triggerElement = trigger;
            const targetName = triggerElement.getAttribute('data-menu-trigger');
            if (!targetName) {
                console.warn(`MenuHandler: New trigger ${index + 1} has no target name specified`, triggerElement);
                errorCount++;
                return;
            }
            const menuElement = this.findMenuTarget(triggerElement, targetName);
            if (!menuElement) {
                console.warn(`MenuHandler: Menu target with data-menu="${targetName}" not found for new trigger ${index + 1}`, triggerElement);
                errorCount++;
                return;
            }
            const existingMenuId = this.findMenuByElement(menuElement);
            if (existingMenuId) {
                console.warn(`MenuHandler: Menu with data-menu="${targetName}" is already registered with another trigger`, {
                    newTrigger: triggerElement,
                    existingMenu: menuElement,
                    existingMenuId
                });
                errorCount++;
                return;
            }
            try {
                this.registerMenuFromElements(triggerElement, menuElement);
                successCount++;
            }
            catch (error) {
                console.error(`MenuHandler: Failed to register new menu for trigger ${index + 1}:`, error);
                errorCount++;
            }
        });
        this.setupCloseButtons();
        console.log(`MenuHandler: Refresh complete - ${successCount} new menus registered, ${errorCount} errors`);
    }
    registerMenu(triggerSelector, targetName, config = {}) {
        const triggerElement = document.querySelector(triggerSelector);
        const menuElement = document.querySelector(`[data-menu="${targetName}"]`);
        if (!triggerElement) {
            throw new Error(`Trigger element "${triggerSelector}" not found`);
        }
        if (!menuElement) {
            throw new Error(`Menu element with data-menu="${targetName}" not found`);
        }
        const menuId = this.generateMenuId(triggerElement, menuElement);
        const mergedConfig = Object.assign(Object.assign({}, this.globalConfig), config);
        const menuState = {
            isOpen: false,
            element: menuElement,
            trigger: triggerElement,
            config: mergedConfig
        };
        this.menus.set(menuId, menuState);
        triggerElement.setAttribute('data-menu-trigger', targetName);
        triggerElement.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (menuState.isOpen) {
                this.closeMenu(menuId);
            }
            else {
                this.closeAllMenus(false);
                this.openMenu(menuId);
            }
        });
        if (!this.isInitialized && this.menus.size === 1) {
            this.setupGlobalListeners();
        }
    }
    destroy() {
        console.log('MenuHandler: Destroying all menus and cleaning up...');
        this.closeAllMenus(false);
        const initializedTriggers = document.querySelectorAll('[data-menu-initialized]');
        initializedTriggers.forEach(trigger => {
            const triggerElement = trigger;
            const handler = triggerElement._menuClickHandler;
            if (handler) {
                triggerElement.removeEventListener('click', handler);
                delete triggerElement._menuClickHandler;
            }
            const focusHandler = triggerElement._menuFocusHandler;
            if (focusHandler) {
                triggerElement.removeEventListener('focusout', focusHandler);
                delete triggerElement._menuFocusHandler;
            }
            triggerElement.removeAttribute('data-menu-initialized');
            triggerElement.removeAttribute('aria-expanded');
            triggerElement.removeAttribute('aria-haspopup');
            triggerElement.removeAttribute('aria-controls');
        });
        this.menus.forEach(menuState => {
            const focusHandler = menuState.element._menuFocusHandler;
            if (focusHandler) {
                menuState.element.removeEventListener('focusout', focusHandler);
                delete menuState.element._menuFocusHandler;
            }
        });
        const initializedCloseButtons = document.querySelectorAll('[data-menu-close-initialized]');
        initializedCloseButtons.forEach(closeButton => {
            const closeButtonElement = closeButton;
            const handler = closeButtonElement._menuCloseHandler;
            if (handler) {
                closeButtonElement.removeEventListener('click', handler);
                delete closeButtonElement._menuCloseHandler;
            }
            closeButtonElement.removeAttribute('data-menu-close-initialized');
        });
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
        if (this.initializationTimeout) {
            clearTimeout(this.initializationTimeout);
            this.initializationTimeout = null;
        }
        this.menus.clear();
        this.cleanupGlobalListeners();
        this.isInitialized = false;
        console.log(`MenuHandler: Cleanup complete - removed ${initializedTriggers.length} trigger markers and ${initializedCloseButtons.length} close button markers`);
    }
    reinit() {
        console.log('MenuHandler: Force re-initialization requested');
        this.destroy();
        this.init();
    }
    registerMenuFromElements(triggerElement, menuElement) {
        if (triggerElement.hasAttribute('data-menu-initialized')) {
            console.warn('MenuHandler: Trigger already initialized, skipping', triggerElement);
            return;
        }
        const menuId = this.generateMenuId(triggerElement, menuElement);
        const config = {
            hiddenClass: triggerElement.getAttribute('data-menu-hidden-class') || this.globalConfig.hiddenClass,
            closeOnOutsideClick: this.parseBoolean(triggerElement.getAttribute('data-menu-close-outside'), this.globalConfig.closeOnOutsideClick),
            closeOnEscape: this.parseBoolean(triggerElement.getAttribute('data-menu-close-escape'), this.globalConfig.closeOnEscape),
            returnFocusOnClose: this.parseBoolean(triggerElement.getAttribute('data-menu-return-focus'), this.globalConfig.returnFocusOnClose)
        };
        const menuState = {
            isOpen: false,
            element: menuElement,
            trigger: triggerElement,
            config
        };
        this.menus.set(menuId, menuState);
        this.debugLog(`Registered menu state for "${triggerElement.getAttribute('data-menu-trigger')}"`, {
            menuId,
            triggerElement,
            menuElement,
            menuState
        });
        const clickHandler = (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (menuState.isOpen) {
                this.closeMenu(menuId);
            }
            else {
                this.closeAllMenus(false);
                this.openMenu(menuId);
            }
        };
        triggerElement._menuClickHandler = clickHandler;
        triggerElement.addEventListener('click', clickHandler);
        this.setupFocusManagement(triggerElement, menuElement, menuId);
        triggerElement.setAttribute('data-menu-initialized', 'true');
        triggerElement.setAttribute('aria-expanded', 'false');
        triggerElement.setAttribute('aria-haspopup', 'true');
        if (!triggerElement.hasAttribute('aria-controls')) {
            triggerElement.setAttribute('aria-controls', menuElement.id || menuId);
        }
    }
    findMenuTarget(triggerElement, targetName) {
        const selector = `[data-menu="${targetName}"]`;
        const triggerContainer = triggerElement.closest(menuContainerSelector);
        if (triggerContainer) {
            return triggerContainer.querySelector(selector);
        }
        return document.querySelector(selector);
    }
    setupFocusManagement(triggerElement, menuElement, menuId) {
        const handleFocusOut = (event) => {
            const menuState = this.menus.get(menuId);
            if (!menuState || !menuState.isOpen)
                return;
            const hasFocusScope = menuElement.hasAttribute('data-menu-focus-scope') &&
                menuElement.getAttribute('data-menu-focus-scope') === 'true';
            setTimeout(() => {
                const newFocusTarget = document.activeElement;
                if (this.globalConfig.debug && menuElement.id === 'mobile-nav') {
                    console.log('[MenuHandler] Focus out from mobile-nav, new target:', newFocusTarget);
                    console.log('[MenuHandler] Focus scope enabled:', hasFocusScope);
                    console.log('[MenuHandler] New target tag:', newFocusTarget === null || newFocusTarget === void 0 ? void 0 : newFocusTarget.tagName);
                }
                if (hasFocusScope) {
                    const isInMenu = menuElement.contains(newFocusTarget);
                    if (this.globalConfig.debug && menuElement.id === 'mobile-nav') {
                        console.log('[MenuHandler] Focus scope check - isInMenu:', isInMenu);
                    }
                    if (!isInMenu && newFocusTarget !== triggerElement && !triggerElement.contains(newFocusTarget)) {
                        if (newFocusTarget && newFocusTarget.tagName !== 'BODY') {
                            this.closeMenu(menuId, false);
                        }
                    }
                    return;
                }
                const isInternalNav = (newFocusTarget === null || newFocusTarget === void 0 ? void 0 : newFocusTarget.hasAttribute('data-nav-internal')) ||
                    (newFocusTarget === null || newFocusTarget === void 0 ? void 0 : newFocusTarget.closest('[data-nav-internal]'));
                if (isInternalNav) {
                    if (this.globalConfig.debug) {
                        console.log('[MenuHandler] Focus on internal navigation control - preserving menu state');
                    }
                    return;
                }
                const isInMenu = menuElement.contains(newFocusTarget);
                const isOnTrigger = triggerElement.contains(newFocusTarget);
                if (this.globalConfig.debug && menuElement.id === 'mobile-nav') {
                    console.log('[MenuHandler] isInMenu:', isInMenu, 'isOnTrigger:', isOnTrigger);
                }
                if (!isInMenu && !isOnTrigger) {
                    this.closeMenu(menuId, false);
                }
            }, 10);
        };
        menuElement.addEventListener('focusout', handleFocusOut);
        triggerElement.addEventListener('focusout', handleFocusOut);
        triggerElement._menuFocusHandler = handleFocusOut;
        menuElement._menuFocusHandler = handleFocusOut;
    }
    setupCloseButtons() {
        const closeButtons = document.querySelectorAll('[data-menu-close]:not([data-menu-close-initialized])');
        closeButtons.forEach((closeButton) => {
            const closeButtonElement = closeButton;
            const targetName = closeButtonElement.getAttribute('data-menu-close');
            const closeHandler = (event) => {
                event.preventDefault();
                event.stopPropagation();
                if (targetName) {
                    const menuId = this.findMenuIdByDataAttribute(targetName, closeButtonElement);
                    if (menuId) {
                        this.closeMenu(menuId);
                    }
                }
                else {
                    const parentMenu = this.findParentMenu(closeButtonElement);
                    if (parentMenu) {
                        this.closeMenu(parentMenu);
                    }
                }
            };
            closeButtonElement._menuCloseHandler = closeHandler;
            closeButtonElement.addEventListener('click', closeHandler);
            closeButtonElement.setAttribute('data-menu-close-initialized', 'true');
        });
    }
    findMenuIdByDataAttribute(targetName, contextElement) {
        let menuElement = null;
        if (contextElement) {
            menuElement = this.findMenuTarget(contextElement, targetName);
        }
        else {
            menuElement = document.querySelector(`[data-menu="${targetName}"]`);
        }
        if (!menuElement)
            return null;
        return this.findMenuByElement(menuElement);
    }
    findParentMenu(element) {
        let current = element.parentElement;
        while (current && current !== document.body) {
            const menuId = this.findMenuByElement(current);
            if (menuId) {
                return menuId;
            }
            current = current.parentElement;
        }
        return null;
    }
    generateMenuId(triggerElement, menuElement) {
        const targetName = triggerElement.getAttribute('data-menu-trigger') || 'unknown';
        const menuName = menuElement.getAttribute('data-menu') || 'unknown';
        const timestamp = Date.now();
        return `${targetName}-${menuName}-${timestamp}`;
    }
    parseBoolean(value, defaultValue = false) {
        if (value === null)
            return defaultValue;
        return value.toLowerCase() === 'true';
    }
    debugLog(message, ...args) {
        if (this.globalConfig.debug) {
            console.log(`[MenuHandler DEBUG] ${message}`, ...args);
        }
    }
    debugError(message, ...args) {
        if (this.globalConfig.debug) {
            console.error(`[MenuHandler DEBUG] ${message}`, ...args);
        }
    }
    getDiagnostics() {
        const allTriggers = document.querySelectorAll('[data-menu-trigger]');
        const allMenus = document.querySelectorAll('[data-menu]');
        const initializedTriggers = document.querySelectorAll('[data-menu-initialized]');
        const uninitializedTriggers = document.querySelectorAll('[data-menu-trigger]:not([data-menu-initialized])');
        const diagnostics = {
            isInitialized: this.isInitialized,
            totalTriggers: allTriggers.length,
            totalMenus: allMenus.length,
            initializedTriggers: initializedTriggers.length,
            uninitializedTriggers: uninitializedTriggers.length,
            registeredMenus: this.menus.size,
            openMenus: this.getOpenMenus().length,
            triggers: Array.from(allTriggers).map(trigger => {
                const triggerElement = trigger;
                const targetName = triggerElement.getAttribute('data-menu-trigger');
                const isInitialized = triggerElement.hasAttribute('data-menu-initialized');
                const targetExists = targetName ? document.querySelector(`[data-menu="${targetName}"]`) !== null : false;
                return {
                    element: triggerElement,
                    targetName,
                    isInitialized,
                    targetExists,
                    ariaExpanded: triggerElement.getAttribute('aria-expanded')
                };
            }),
            menus: Array.from(allMenus).map(menu => {
                const menuElement = menu;
                const menuName = menuElement.getAttribute('data-menu');
                const isHidden = menuElement.classList.contains(this.globalConfig.hiddenClass);
                return {
                    element: menuElement,
                    menuName,
                    isHidden,
                    classList: Array.from(menuElement.classList)
                };
            })
        };
        return diagnostics;
    }
    debugMenuPairing(triggerSelector) {
        var _a;
        const trigger = document.querySelector(triggerSelector);
        if (!trigger) {
            console.error(`Trigger not found: ${triggerSelector}`);
            return;
        }
        const targetName = trigger.getAttribute('data-menu-trigger');
        if (!targetName) {
            console.error(`Trigger has no data-menu-trigger attribute`);
            return;
        }
        console.log(`=== Debug Menu Pairing: ${targetName} ===`);
        console.log(`Trigger element:`, trigger);
        console.log(`Target name: ${targetName}`);
        console.log(`Trigger parent:`, trigger.parentElement);
        console.log(`Trigger siblings:`, Array.from(((_a = trigger.parentElement) === null || _a === void 0 ? void 0 : _a.children) || []));
        let container = trigger.parentElement;
        while (container && container !== document.body) {
            if (container.hasAttribute(menuContainerSelector)) {
                console.log(`Found container:`, container);
                console.log(`Container name:`, container.getAttribute(menuContainerSelector));
                break;
            }
            container = container.parentElement;
        }
        const foundTarget = this.findMenuTarget(trigger, targetName);
        console.log(`Found target:`, foundTarget);
        if (foundTarget) {
            console.log(`Target parent:`, foundTarget.parentElement);
            console.log(`Target classes:`, Array.from(foundTarget.classList));
        }
        const documentTarget = document.querySelector(`[data-menu="${targetName}"]`);
        console.log(`Document target:`, documentTarget);
        const existingMenuId = foundTarget ? this.findMenuByElement(foundTarget) : null;
        console.log(`Existing menu ID:`, existingMenuId);
        console.log(`=== End Debug ===`);
    }
    setupGlobalListeners() {
        this.outsideClickListener = (event) => {
            const target = event.target;
            const isInternalNav = (target === null || target === void 0 ? void 0 : target.hasAttribute('data-nav-internal')) ||
                (target === null || target === void 0 ? void 0 : target.closest('[data-nav-internal]'));
            if (isInternalNav) {
                if (this.globalConfig.debug) {
                    console.log('[MenuHandler] Click on internal navigation control - skipping close check');
                }
                return;
            }
            for (const [menuId, menuState] of this.menus) {
                if (menuState.isOpen && menuState.config.closeOnOutsideClick) {
                    if (!menuState.element.contains(target) && !menuState.trigger.contains(target)) {
                        this.closeMenu(menuId);
                    }
                }
            }
        };
        this.escapeKeyListener = (event) => {
            if (event.key === 'Escape') {
                const openMenus = this.getOpenMenus();
                for (const menuId of openMenus) {
                    const menuState = this.menus.get(menuId);
                    if (menuState === null || menuState === void 0 ? void 0 : menuState.config.closeOnEscape) {
                        this.closeMenu(menuId);
                    }
                }
            }
        };
        document.addEventListener('click', this.outsideClickListener);
        document.addEventListener('keydown', this.escapeKeyListener);
    }
    cleanupGlobalListeners() {
        if (this.outsideClickListener) {
            document.removeEventListener('click', this.outsideClickListener);
            this.outsideClickListener = null;
        }
        if (this.escapeKeyListener) {
            document.removeEventListener('keydown', this.escapeKeyListener);
            this.escapeKeyListener = null;
        }
    }
    dispatchMenuEvent(menuId, action, menuState) {
        const customEvent = new CustomEvent(`menu:${action}`, {
            detail: {
                menuId,
                element: menuState.element,
                trigger: menuState.trigger
            }
        });
        document.dispatchEvent(customEvent);
    }
}
const isProduction = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';
export const menuHandler = new MenuHandler({ debug: !isProduction });
export const initMenus = (config) => {
    const handler = new MenuHandler(config);
    handler.init();
};
export const refreshMenus = () => {
    menuHandler.refresh();
};
export const reinitMenus = () => {
    menuHandler.reinit();
};
